import React, { useEffect, useState } from 'react';
import { api } from '../../api/client';
import type { User } from '../../api/types';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { AsyncButton } from '../../components/AsyncButton';

export function AdminEmployeesPage() {
  const { push } = useToast();
  const [q, setQ] = useState('');
  const [items, setItems] = useState<User[]>([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState<User | null>(null);

  const [first_name, setFirst] = useState('');
  const [last_name, setLast] = useState('');
  const [username, setUsern] = useState('');
  const [password, setPass] = useState('');
  const [is_active, setActive] = useState(true);

  async function load() {
    const r = await api.get<{users: User[]}>(`/api/admin/users?query=${encodeURIComponent(q.trim())}`);
    setItems(r.users);
  }

  useEffect(() => { load(); }, []);

  function openCreate() {
    setEdit(null);
    setFirst(''); setLast(''); setUsern(''); setPass(''); setActive(true);
    setOpen(true);
  }

  function openEdit(u: User) {
    setEdit(u);
    setFirst(u.first_name); setLast(u.last_name); setUsern(u.username); setActive(u.is_active);
    setPass('');
    setOpen(true);
  }

  async function save() {
    try {
      if (!first_name || !last_name || !username) {
        push({ title: 'Compila i campi obbligatori' });
        return;
      }
      if (!edit && password.length < 8) {
        push({ title: 'Password troppo corta', body: 'Minimo 8 caratteri.' });
        return;
      }

      if (!edit) {
        await api.post('/api/admin/users', { first_name, last_name, username, password, is_active });
        push({ title: 'Dipendente creato' });
      } else {
        await api.patch(`/api/admin/users/${edit._id}`, { first_name, last_name, username, is_active });
        push({ title: 'Dipendente aggiornato' });
      }

      setOpen(false);
      await load();
    } catch (e:any) {
      push({ title: 'Errore', body: e?.data?.error || e?.message });
    }
  }

  async function resetPassword() {
    if (!edit) return;
    if (password.length < 8) {
      push({ title: 'Password troppo corta' });
      return;
    }
    try {
      await api.post(`/api/admin/users/${edit._id}/reset-password`, { newPassword: password });
      push({ title: 'Password resettata' });
      setPass('');
    } catch (e:any) {
      push({ title: 'Errore reset', body: e?.data?.error || e?.message });
    }
  }

  return (
    <div className="grid" style={{ gap: 16 }}>
      <div className="card">
        <div className="spread">
          <div>
            <div className="h2" style={{ marginTop:0 }}>Dipendenti</div>
            <div className="small">Crea, modifica e attiva/disattiva utenti.</div>
          </div>
          <button className="btn btnPrimary" onClick={openCreate}>+ Nuovo</button>
        </div>

        <div className="row" style={{ marginTop: 12 }}>
          <input className="input" placeholder="Cerca…" value={q} onChange={(e) => setQ(e.target.value)} />
          <AsyncButton className="btn btnSecondary" onClickAsync={load} busyText="Cerco…">Cerca</AsyncButton>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow:'hidden' }}>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'var(--black)', color:'var(--white)' }}>
                <th style={{ padding: 12, textAlign:'left' }}>Nome</th>
                <th style={{ padding: 12, textAlign:'left' }}>Username</th>
                <th style={{ padding: 12, textAlign:'left' }}>Stato</th>
                <th style={{ padding: 12 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((u, idx) => (
                <tr key={u._id} style={{ background: idx % 2 ? 'rgba(255,255,255,.02)' : 'rgba(255,255,255,.04)' }}>
                  <td style={{ padding: 12, fontWeight: 800 }}>{u.first_name} {u.last_name}</td>
                  <td style={{ padding: 12 }}>{u.username}</td>
                  <td style={{ padding: 12 }}>
                    <span className={`badge ${u.is_active ? 'badgeGreen' : 'badgeRed'}`}>{u.is_active ? 'Attivo' : 'Disattivo'}</span>
                  </td>
                  <td style={{ padding: 12, textAlign:'right' }}>
                    <button className="btn btnGhost" onClick={() => openEdit(u)}>Modifica</button>
                  </td>
                </tr>
              ))}
              {items.length === 0 ? <tr><td colSpan={4} className="small" style={{ padding: 16 }}>Nessun dipendente.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? 'Modifica dipendente' : 'Nuovo dipendente'}>
        <div className="grid" style={{ gap: 10 }}>
          <div className="grid" style={{ gridTemplateColumns:'1fr 1fr', gap: 10 }}>
            <div>
              <div className="small">Nome</div>
              <input className="input" value={first_name} onChange={(e) => setFirst(e.target.value)} />
            </div>
            <div>
              <div className="small">Cognome</div>
              <input className="input" value={last_name} onChange={(e) => setLast(e.target.value)} />
            </div>
          </div>
          <div>
            <div className="small">Username</div>
            <input className="input" value={username} onChange={(e) => setUsern(e.target.value)} />
          </div>

          <label className="row small">
            <input type="checkbox" checked={is_active} onChange={(e) => setActive(e.target.checked)} />
            Attivo
          </label>

          {!edit ? (
            <div>
              <div className="small">Password iniziale</div>
              <input className="input" type="password" value={password} onChange={(e) => setPass(e.target.value)} />
            </div>
          ) : (
            <div className="card" style={{ background:'var(--gray-200)', boxShadow:'none' }}>
              <div className="small" style={{ marginBottom: 6 }}>Reset password (opzionale)</div>
              <input className="input" type="password" placeholder="Nuova password" value={password} onChange={(e) => setPass(e.target.value)} />
              <AsyncButton className="btn btnSecondary" style={{ marginTop: 10 }} onClickAsync={resetPassword} busyText="Resetto…">Reset</AsyncButton>
            </div>
          )}

          <div className="spread" style={{ marginTop: 10 }}>
            <button className="btn btnSecondary" onClick={() => setOpen(false)}>Annulla</button>
            <AsyncButton className="btn btnPrimary" onClickAsync={save} busyText="Salvo…">Salva</AsyncButton>
          </div>
        </div>
      </Modal>
    </div>
  );
}
